import type { GameStateData } from '../game/GameState.js';
import type { Pip } from '../game/types.js';
import { openPipValues } from './SuitTracker.js';

/**
 * Bayesian-lite inference over what each seat cannot hold (per H7+H8 in the
 * AI research summary). V1 derivation is intentionally narrow: we only use
 * the `consecutivePasses` window that ends at the current seat.
 *
 * Soundness: passes do not mutate the chain; draws also do not (they only
 * add to the passer's hand). So if seats S, S+1, …, currentSeat-1 all passed
 * in a row, every one of them faced the *current* chain open values and had
 * no legal play after exhausting the boneyard (under `draw-until-playable`)
 * or simply after one draw / no draw (under the other variants). Therefore
 * each passer holds zero tiles matching any currently-open pip.
 *
 * The inference is wiped the moment any seat plays, since the open pips
 * change and the passer may have drawn additional tiles. This keeps the
 * tracker truly stateless (a pure function of `state`) — Hard tier (M7)
 * will replace this with a proper Bayesian belief tracker that survives
 * across plays.
 */
export type SeatSuitConstraints = readonly (ReadonlySet<Pip>)[];

const EMPTY: ReadonlySet<Pip> = new Set();

export function deriveConstraints(state: GameStateData): SeatSuitConstraints {
  const result: ReadonlySet<Pip>[] = new Array<ReadonlySet<Pip>>(state.playerCount).fill(EMPTY);
  const passes = state.consecutivePasses;
  if (passes <= 0) return result;

  const openPips = openPipValues(state);
  if (openPips.length === 0) return result;
  const forbidden = new Set<Pip>(openPips);

  // The seat that passed `k`-th most recently is `currentSeat - k - 1`
  // (mod playerCount), since `applyPass` advances the seat by 1.
  for (let k = 0; k < passes; k += 1) {
    const seat = (state.currentSeat - k - 1 + state.playerCount * (k + 1)) % state.playerCount;
    result[seat] = forbidden;
  }
  return result;
}
